import { ActionIcon, Alert, Button, Group, Modal, NumberInput, Stack, Table, Text, TextInput } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { IconPencil, IconPlus, IconTrash } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, izvedi, podatki, type Shema } from '../api/odjemalec';
import { NapakaApi, sporocilo } from '../api/napaka';
import { datum, datumIzMs, denar, msIzDatuma, odstotek } from '../oblika/oblika';
import { Kartica } from '../postavitev/Podatki';
import { stevilo } from './vozilo';
import { PomocIkona } from '../pomoc/PomocIkona';

type Strosek = Shema['DodatniStrosekPodatki'];

/**
 * Dodatni stroški vozila (kot tabela in DodajStrosekDialog v PodrobnostiVozilaDialog): dodajanje, urejanje in brisanje.
 * Vnos je znesek z DDV in stopnja DDV; neto in DDV izračuna strežnik, neto je del nabavne cene (tudi to izračuna
 * strežnik po vsaki spremembi).
 */
export function StroskiVozila({ voziloId, stroski, skupaj }: { voziloId: number; stroski: Strosek[]; skupaj: number }) {
  const odjemalec = useQueryClient();
  const [urejan, setUrejan] = useState<Strosek | null>(null);
  const [brisem, setBrisem] = useState<number | null>(null);
  const [napaka, setNapaka] = useState<string | null>(null);

  const osvezi = () => odjemalec.invalidateQueries({ queryKey: ['vozilo', voziloId] });

  const izbrisi = async (s: Strosek) => {
    setNapaka(null);
    setBrisem(s.id!);
    try {
      await izvedi(api.DELETE('/api/stroski/{id}', { params: { path: { id: s.id! } } }));
      await osvezi();
      notifications.show({ color: 'green', message: `Strošek »${s.opis || 'brez opisa'}« je odstranjen.` });
    } catch (e) {
      setNapaka(sporocilo(e));
    } finally {
      setBrisem(null);
    }
  };

  return (
    <Kartica
      naslov="Dodatni stroški"
      dejanja={
        <Group gap={4} wrap="nowrap">
          <PomocIkona clanek="stroski-vozila" />
          <Button
            size="xs"
            variant="light"
            leftSection={<IconPlus size={14} />}
            onClick={() => setUrejan({ datum: Date.now(), neto: 0, ddv: 0, opis: '', voziloId, ddvStopnja: 22 })}
          >
            Dodaj strošek
          </Button>
        </Group>
      }
    >
      {napaka && (
        <Alert color="red" variant="light" mb="sm" role="alert">
          {napaka}
        </Alert>
      )}
      {stroski.length === 0 ? (
        <Text size="sm" c="dimmed">
          Brez dodatnih stroškov.
        </Text>
      ) : (
        <Table.ScrollContainer minWidth={520}>
          <Table className="stevilke" verticalSpacing={6}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Opis</Table.Th>
                <Table.Th>Datum</Table.Th>
                <Table.Th ta="right">Z DDV</Table.Th>
                <Table.Th ta="right">DDV %</Table.Th>
                <Table.Th ta="right">DDV</Table.Th>
                <Table.Th ta="right">Neto</Table.Th>
                <Table.Th w={80} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {stroski.map((s) => (
                <Table.Tr key={s.id}>
                  <Table.Td>{s.opis}</Table.Td>
                  <Table.Td className="brez-preloma">{datum(datumIzMs(s.datum))}</Table.Td>
                  <Table.Td ta="right" className="brez-preloma" fw={600}>
                    {denar(s.skupaj)}
                  </Table.Td>
                  <Table.Td ta="right" className="brez-preloma">
                    {s.ddvStopnja == null ? '' : `${odstotek(s.ddvStopnja)} %`}
                  </Table.Td>
                  <Table.Td ta="right" className="brez-preloma">
                    {denar(s.ddv)}
                  </Table.Td>
                  <Table.Td ta="right" className="brez-preloma">
                    {denar(s.neto)}
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4} wrap="nowrap" justify="flex-end">
                      <ActionIcon variant="subtle" aria-label={`Uredi strošek ${s.opis}`} onClick={() => setUrejan(s)}>
                        <IconPencil size={16} />
                      </ActionIcon>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        aria-label={`Odstrani strošek ${s.opis}`}
                        loading={brisem === s.id}
                        onClick={() => void izbrisi(s)}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
      {stroski.length > 0 && (
        <Text size="sm" ta="right" mt="xs">
          Skupaj z DDV: <b>{denar(skupaj)} €</b>
        </Text>
      )}
      <StrosekOkno
        strosek={urejan}
        zapri={() => setUrejan(null)}
        shranjen={async () => {
          setUrejan(null);
          await osvezi();
        }}
      />
    </Kartica>
  );
}

/** Nov ali urejan strošek: opis, datum, znesek z DDV in stopnja DDV (neto in DDV izračuna strežnik). */
function StrosekOkno({
  strosek,
  zapri,
  shranjen,
}: {
  strosek: Strosek | null;
  zapri: () => void;
  shranjen: () => Promise<void>;
}) {
  return (
    <Modal
      opened={strosek !== null}
      onClose={zapri}
      title={strosek?.id ? 'Uredi strošek' : 'Dodaj strošek'}
      radius="lg"
    >
      {strosek && <ObrazecStroska key={strosek.id ?? 'nov'} strosek={strosek} zapri={zapri} shranjen={shranjen} />}
    </Modal>
  );
}

function ObrazecStroska({
  strosek,
  zapri,
  shranjen,
}: {
  strosek: Strosek;
  zapri: () => void;
  shranjen: () => Promise<void>;
}) {
  const [opis, setOpis] = useState(strosek.opis ?? '');
  const [dan, setDan] = useState<string | null>(datumIzMs(strosek.datum) || null);
  const [bruto, setBruto] = useState<number | string>(strosek.bruto ?? strosek.skupaj ?? 0);
  const [stopnja, setStopnja] = useState<number | string>(strosek.ddvStopnja ?? 22);
  const [napaka, setNapaka] = useState<string | null>(null);
  const [polja, setPolja] = useState<Record<string, string>>({});
  const [dela, setDela] = useState(false);

  const shrani = async () => {
    if (!dan) {
      setNapaka('Izberite datum stroška.');
      return;
    }
    setNapaka(null);
    setPolja({});
    setDela(true);
    const telo: Strosek = {
      ...strosek,
      opis: opis.trim(),
      datum: msIzDatuma(dan),
      bruto: stevilo(bruto) ?? 0,
      ddvStopnja: stevilo(stopnja) ?? undefined,
    };
    try {
      if (strosek.id) {
        await podatki(api.PUT('/api/stroski/{id}', { params: { path: { id: strosek.id } }, body: telo }));
      } else {
        await podatki(
          api.POST('/api/vozila/{id}/stroski', { params: { path: { id: strosek.voziloId! } }, body: telo }),
        );
      }
      notifications.show({ color: 'green', message: strosek.id ? 'Strošek je shranjen.' : 'Strošek je dodan.' });
      await shranjen();
    } catch (e) {
      if (e instanceof NapakaApi && Object.keys(e.polja).length > 0) {
        setPolja(e.polja);
      } else {
        setNapaka(sporocilo(e));
      }
      setDela(false);
    }
  };

  const znesek = { decimalScale: 2, decimalSeparator: ',', thousandSeparator: '.', rightSection: '€' } as const;
  return (
    <Stack gap="sm">
      <TextInput
        label="Opis"
        placeholder="npr. servis, registracija, čiščenje"
        value={opis}
        onChange={(e) => setOpis(e.currentTarget.value)}
        data-autofocus
      />
      <DatePickerInput label="Datum" value={dan} onChange={setDan} valueFormat="D. M. YYYY" withAsterisk />
      <Group grow align="flex-start">
        <NumberInput
          label="Znesek z DDV"
          value={bruto}
          onChange={setBruto}
          error={polja.bruto}
          withAsterisk
          {...znesek}
        />
        <NumberInput
          label="Stopnja DDV"
          value={stopnja}
          onChange={setStopnja}
          error={polja.ddvStopnja}
          decimalScale={1}
          decimalSeparator=","
          min={0}
          max={100}
          rightSection="%"
          withAsterisk
        />
      </Group>
      <Text size="xs" c="dimmed">
        Neto znesek in DDV izračuna program; neto se prišteje nabavni ceni vozila.
      </Text>
      {napaka && (
        <Alert color="red" variant="light" role="alert">
          {napaka}
        </Alert>
      )}
      <Group justify="flex-end" gap="xs">
        <Button variant="subtle" onClick={zapri} disabled={dela}>
          Prekliči
        </Button>
        <Button onClick={() => void shrani()} loading={dela}>
          {strosek.id ? 'Shrani' : 'Dodaj'}
        </Button>
      </Group>
    </Stack>
  );
}
